import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Scanner } from '@yudiel/react-qr-scanner';
import { CheckCircle, XCircle, Search, Users, Clock, AlertTriangle, Camera, Wifi, WifiOff, BarChart } from 'lucide-react';
import { checkInService } from '../../services/checkInService';
import { eventService } from '../../services/eventService';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';

export default function OrganizerCheckInInterface() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State management
  const [event, setEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState({ total: 0, checkedIn: 0, pending: 0, percentage: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [scanError, setScanError] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showStats, setShowStats] = useState(false);
  const [offlineQueue, setOfflineQueue] = useState([]);

  // Load event and registrations
  useEffect(() => {
    loadEventData();
    loadStats();
  }, [eventId]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!eventId) return;
    
    const subscription = checkInService?.subscribeToCheckIns(eventId, (update) => {
      setRegistrations(prev => prev?.map(reg => 
        reg?.id === update?.id 
          ? { ...reg, checkedIn: update?.checkedIn, checkedInAt: update?.checkedInAt }
          : reg
      ));
      loadStats();
    });

    return () => {
      if (subscription) {
        subscription?.unsubscribe();
      }
    };
  }, [eventId]);

  const loadEventData = async () => {
    try {
      setLoading(true);
      const eventData = await eventService?.getById(eventId);
      setEvent(eventData);
      
      const regs = await checkInService?.getEventRegistrations(eventId);
      setRegistrations(regs);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await checkInService?.getCheckInStats(eventId);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const handleScan = async (result) => {
    if (!result || !result?.[0]) return;
    
    try {
      setIsScanning(false);
      setScanError('');
      
      // Extract registration ID from QR data
      const registrationId = result?.[0]?.rawValue;
      
      // Perform check-in
      const checkInResult = await checkInService?.checkInByQR(registrationId);
      
      if (checkInResult?.success) {
        setScanResult({
          type: 'success',
          data: checkInResult
        });
        
        // Vibrate on success (if supported)
        if (navigator.vibrate) {
          navigator.vibrate(200);
        }
        
        // Reload data
        await loadEventData();
        await loadStats();
        
        // Clear result after 3 seconds
        setTimeout(() => {
          setScanResult(null);
          setIsScanning(true);
        }, 3000);
      } else {
        setScanResult({
          type: 'error',
          message: checkInResult?.error,
          isDuplicate: checkInResult?.duplicate
        });
        
        // Clear error after 3 seconds
        setTimeout(() => {
          setScanResult(null);
          setIsScanning(true);
        }, 3000);
      }
    } catch (err) {
      setScanError(err?.message);
      setScanResult({
        type: 'error',
        message: err?.message
      });
      
      setTimeout(() => {
        setScanResult(null);
        setIsScanning(true);
      }, 3000);
    }
  };

  const handleScanError = (error) => {
    console.error('QR Scan Error:', error);
    setScanError('Failed to access camera. Please check permissions.');
  };

  const handleManualCheckIn = async (registrationId) => {
    try {
      const result = await checkInService?.checkInByQR(registrationId);
      
      if (result?.success) {
        await loadEventData();
        await loadStats();
      } else {
        alert(result?.error);
      }
    } catch (err) {
      alert(err?.message);
    }
  };

  const filteredRegistrations = registrations?.filter(reg =>
    reg?.participantName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    reg?.participantEmail?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="p-4 text-red-600">Error: {error}</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Check-In - {event?.title || 'Event'}</title>
      </Helmet>
      {/* Header with stats */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{event?.title}</h1>
              <p className="text-sm text-gray-600">{event?.eventDate} at {event?.eventTime}</p>
            </div>
            <button
              onClick={() => setShowStats(!showStats)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
            >
              <BarChart className="w-5 h-5" />
              Stats
            </button>
          </div>

          {/* Quick stats */}
          {showStats && (
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">Total</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{stats?.total}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-green-600 mb-1">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">Checked In</span>
                </div>
                <p className="text-2xl font-bold text-green-700">{stats?.checkedIn}</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-orange-600 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">Pending</span>
                </div>
                <p className="text-2xl font-bold text-orange-700">{stats?.pending}</p>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Online/Offline indicator */}
      <div className={`px-4 py-2 text-center text-sm ${isOnline ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
        {isOnline ? (
          <div className="flex items-center justify-center gap-2">
            <Wifi className="w-4 h-4" />
            Online - Real-time sync active
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <WifiOff className="w-4 h-4" />
            Offline - Check-ins will sync when connection resumes
          </div>
        )}
      </div>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* QR Scanner Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Camera className="w-6 h-6 text-blue-600" />
                QR Code Scanner
              </h2>
              <button
                onClick={() => setIsScanning(!isScanning)}
                className={`px-4 py-2 rounded-lg ${
                  isScanning
                    ? 'bg-red-600 text-white hover:bg-red-700' :'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isScanning ? 'Stop' : 'Start'} Scanning
              </button>
            </div>

            {/* Scanner viewport */}
            <div className="relative bg-gray-900 rounded-lg overflow-hidden" style={{ aspectRatio: '1/1' }}>
              {isScanning ? (
                <>
                  <Scanner
                    onScan={handleScan}
                    onError={handleScanError}
                    constraints={{ 
                      facingMode: 'environment',
                      aspectRatio: 1
                    }}
                    styles={{
                      container: { width: '100%', height: '100%' }
                    }}
                  />
                  {/* Scanning overlay */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-64 h-64 border-4 border-blue-500 rounded-lg animate-pulse"></div>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <div className="text-center">
                    <Camera className="w-16 h-16 mx-auto mb-2" />
                    <p>Click "Start Scanning" to begin</p>
                  </div>
                </div>
              )}
            </div>

            {/* Scan result feedback */}
            {scanResult && (
              <div className={`mt-4 p-4 rounded-lg ${
                scanResult?.type === 'success' ?'bg-green-50 border border-green-200' :'bg-red-50 border border-red-200'
              }`}>
                {scanResult?.type === 'success' ? (
                  <div>
                    <div className="flex items-center gap-2 text-green-700 font-semibold mb-2">
                      <CheckCircle className="w-6 h-6" />
                      Check-in Successful!
                    </div>
                    <div className="text-sm text-green-900">
                      <p className="font-medium">{scanResult?.data?.attendee?.name}</p>
                      <p className="text-green-700">{scanResult?.data?.attendee?.email}</p>
                      {scanResult?.data?.attendee?.equipment_rental && (
                        <p className="mt-2 flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" />
                          Equipment rental required
                        </p>
                      )}
                      {scanResult?.data?.attendee?.special_requirements && (
                        <p className="mt-1 text-xs text-green-700">
                          Note: {scanResult?.data?.attendee?.special_requirements}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-700">
                    <XCircle className="w-6 h-6" />
                    <div>
                      <p className="font-semibold">
                        {scanResult?.isDuplicate ? 'Already Checked In' : 'Check-in Failed'}
                      </p>
                      <p className="text-sm">{scanResult?.message}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {scanError && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {scanError}
              </div>
            )}
          </div>

          {/* Attendee list section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-blue-600" />
              Attendee List
            </h2>

            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Attendee list */}
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {filteredRegistrations?.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No attendees found</p>
              ) : (
                filteredRegistrations?.map((reg) => (
                  <div
                    key={reg?.id}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      reg?.checkedIn
                        ? 'border-green-200 bg-green-50' :'border-gray-200 bg-white hover:border-blue-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{reg?.participantName}</p>
                        <p className="text-sm text-gray-600">{reg?.participantEmail}</p>
                        {reg?.participantPhone && (
                          <p className="text-sm text-gray-500">{reg?.participantPhone}</p>
                        )}
                        {reg?.equipmentRental && (
                          <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Equipment rental
                          </p>
                        )}
                        {reg?.specialRequirements && (
                          <p className="text-xs text-gray-500 mt-1">
                            Note: {reg?.specialRequirements}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {reg?.checkedIn ? (
                          <div className="flex items-center gap-1 text-green-600 text-sm">
                            <CheckCircle className="w-5 h-5" />
                            <span>Checked In</span>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleManualCheckIn(reg?.id)}
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                          >
                            Check In
                          </button>
                        )}
                        {reg?.checkedInAt && (
                          <p className="text-xs text-gray-500">
                            {new Date(reg.checkedInAt)?.toLocaleTimeString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}